import { type Ref } from 'vue/dist/vue.d.ts';
import { type Actions, type Getters, type State } from './types';

type ItineraryStateModule<T> = {
    [key in keyof T]: T[key] extends Function ? T[key] : Ref<T[key]>;
};

function extractGetter<T extends object>(cls: T): Getters<T> {
    const getterEntries: string[] = [];
    const instance = cls as any;
    let proto = Object.getPrototypeOf(instance);
    while (proto.constructor !== Object) {
        getterEntries.push(
            ...Object.entries(Object.getOwnPropertyDescriptors(proto))
                .filter(
                    ([, descriptor]) => typeof descriptor.get === 'function'
                )
                .map(([key]) => key)
        );
        proto = Object.getPrototypeOf(proto);
    }
    return getterEntries.reduceRight(
        (prev, currKey) => ({
            ...prev,
            [currKey]: computed(() => instance[currKey]),
        }),
        {} as Record<
            keyof Getters<T>,
            (state: State<T>) => Getters<T>[keyof Getters<T>]
        >
    ) as any;
}

function extractState<T extends object>(cls: T): State<T> {
    const instance = cls as any;
    const stateEntries = Object.getOwnPropertyNames(cls);
    return stateEntries.reduce(
        (prev, currKey) => ({
            ...prev,
            [currKey]: computed({
                get() {
                    return instance[currKey];
                },
                set(value: any) {
                    instance[currKey] = value;
                },
            }),
        }),
        {} as Record<keyof State<T>, State<T>>
    ) as any;
}

export function extractActions<T extends object>(cls: T): Actions<T> {
    const actionEntries: string[] = [];
    const instance = cls as any;
    let proto = Object.getPrototypeOf(instance);
    while (proto.constructor !== Object) {
        actionEntries.push(
            ...Object.entries(Object.getOwnPropertyDescriptors(proto))
                .filter(
                    ([, descriptor]) => typeof descriptor.value === 'function'
                )
                .filter(([key]) => key !== 'constructor')
                .map(([key]) => key)
        );
        proto = Object.getPrototypeOf(proto);
    }
    return actionEntries.reduceRight(
        (prev, currentValue) => ({
            ...prev,
            [currentValue]: (...args: any) =>
                instance[currentValue].call(cls, ...args),
        }),
        {} as Record<keyof Actions<T>, Actions<T>>
    ) as any;
}

export function moduleToRefs<T extends object>(
    cls: T
): ItineraryStateModule<T> {
    const states = extractState(cls);
    const getters = extractGetter(cls);
    const stateModule = {
        ...states,
        ...getters,
    };
    return stateModule as unknown as ItineraryStateModule<T>;
}
